/**
 * The onboarding shell.
 *
 * The survey canvas is only part of the screen. Around it the respondent always sees which
 * business area they are in, why it is being asked, how much is left, what can be skipped, and
 * what we already learned from the files they uploaded.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { sessions as sessionsApi, uploads as uploadsApi } from "../api/resources";
import { AssistantPanel } from "../components/assistant/AssistantPanel";
import { SectionNavigator } from "../components/SectionNavigator";
import { SurveyRunner, type SaveStatus, type SurveyControl } from "../components/SurveyRunner";
import { Card, ErrorBanner, Loading, PageHeader, Progress, StatusBadge } from "../components/ui";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { displayValue, relativeTime } from "../lib/format";
import { uploadSummary, type SurveyProgress } from "../lib/surveyProgress";
import { useAsync } from "../lib/useAsync";
import type { PipelineResult } from "../lib/types";

const SAVE_LABEL: Record<SaveStatus, string> = {
  idle: "Nothing to save yet",
  saving: "Saving…",
  saved: "All answers saved",
  error: "Save failed",
};

const DEPTH_KEY = "discovery.deepDive";

export function InterviewPage() {
  const { sessionId } = useParams();
  const { organizationId } = useCurrentOrganization();
  const navigate = useNavigate();

  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [saveError, setSaveError] = useState<string | undefined>();
  const [pipeline, setPipeline] = useState<PipelineResult | null>();
  const [showAnswers, setShowAnswers] = useState(false);
  // The coach is opt-in and unobtrusive: it appears beside the questions when asked for, and
  // never touches an answer without the respondent pressing Use draft.
  const [coachOpen, setCoachOpen] = useState(false);
  const [progress, setProgress] = useState<SurveyProgress | undefined>();
  const [deepDive, setDeepDive] = useState(
    () => localStorage.getItem(DEPTH_KEY) === "true",
  );
  const control = useRef<SurveyControl | null>(null);

  const session = useAsync(
    () =>
      organizationId && sessionId
        ? sessionsApi.get(organizationId, sessionId)
        : Promise.resolve(undefined as never),
    [organizationId, sessionId],
  );

  const uploads = useAsync(
    () =>
      organizationId
        ? uploadsApi.list(organizationId, { limit: 200 })
        : Promise.resolve({ items: [], total: 0, limit: 0, offset: 0 }),
    [organizationId],
  );

  const responses = useAsync(
    () =>
      organizationId && sessionId && showAnswers
        ? sessionsApi.responses(organizationId, sessionId)
        : Promise.resolve([]),
    [organizationId, sessionId, showAnswers],
  );

  const handleSave = useCallback(
    async (payload: {
      data: Record<string, unknown>;
      currentPageNo: number;
      progressPercent: number;
    }) => {
      if (!organizationId || !sessionId) return;
      await sessionsApi.saveState(organizationId, sessionId, {
        data: payload.data,
        current_page_no: payload.currentPageNo,
        progress_percent: payload.progressPercent,
      });
    },
    [organizationId, sessionId],
  );

  const handleComplete = useCallback(
    async (payload: { data: Record<string, unknown>; currentPageNo: number }) => {
      if (!organizationId || !sessionId) return;
      const result = await sessionsApi.submit(organizationId, sessionId, {
        data: payload.data,
        current_page_no: payload.currentPageNo,
      });
      setPipeline(result.pipeline);
      session.reload();
    },
    [organizationId, sessionId, session],
  );

  const handleStatus = useCallback((status: SaveStatus, message?: string) => {
    setSaveStatus(status);
    setSaveError(message);
  }, []);

  const uploadItems = useMemo(() => uploads.data?.items ?? [], [uploads.data]);
  const learned = useMemo(() => uploadSummary(uploadItems), [uploadItems]);

  const currentSection = progress?.sections.find(
    (section) => section.key === progress.currentSectionKey,
  );
  const nextSection = useMemo(() => {
    if (!progress) return undefined;
    const index = progress.sections.findIndex((s) => s.key === progress.currentSectionKey);
    return index >= 0 ? progress.sections[index + 1] : undefined;
  }, [progress]);

  if (!organizationId) return <Card>Select an organization first.</Card>;
  if (session.loading) return <Loading label="Loading interview…" />;
  if (session.error) return <ErrorBanner error={session.error} />;
  if (!session.data) return null;

  const detail = session.data;
  const isComplete = detail.status === "completed";

  const toggleDepth = () => {
    const next = !deepDive;
    setDeepDive(next);
    localStorage.setItem(DEPTH_KEY, String(next));
  };

  return (
    <>
      <PageHeader
        title={detail.title}
        description={
          progress
            ? `${progress.answered} of ${progress.total} questions answered · ${progress.requiredTotal - progress.requiredAnswered} required left${
                progress.skippedByBranching
                  ? ` · ${progress.skippedByBranching} skipped because they don't apply to you`
                  : ""
              }`
            : `${detail.module_keys.length} areas · ${detail.response_count} answers recorded`
        }
        actions={
          <>
            <span className={`autosave autosave--${saveStatus}`}>
              <span className="autosave__dot" />
              {SAVE_LABEL[saveStatus]}
            </span>
            {!isComplete ? (
              <button
                onClick={async () => {
                  await sessionsApi.pause(organizationId, detail.id);
                  navigate("/interviews");
                }}
              >
                Pause &amp; exit
              </button>
            ) : null}
            <button
              type="button"
              className="btn"
              onClick={() => setCoachOpen((open) => !open)}
            >
              {coachOpen ? "Hide assistant" : "Ask Discovery Assistant"}
            </button>
            <Link className="btn" to="/onboarding/review">
              Review what we know
            </Link>
          </>
        }
      />

      <div className="stack">
        <ErrorBanner error={saveError} />

        <Card>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <div className="row">
              <StatusBadge status={detail.status} />
              <span className="small muted">
                Last activity {relativeTime(detail.last_activity_at)}
              </span>
              <button className="btn--small" onClick={toggleDepth} title="Switch how much we ask">
                {deepDive ? "Full discovery" : "Essentials only"} — switch
              </button>
            </div>
            <div style={{ width: 280 }}>
              <Progress value={progress?.percent ?? detail.progress_percent} />
            </div>
          </div>
          <p className="small muted" style={{ margin: "8px 0 0" }}>
            {deepDive
              ? "Full discovery asks everything, including the detail sections. You can switch back at any time."
              : "Essentials only asks what is needed to configure your systems. The deeper sections stay available for later."}
          </p>
        </Card>

        {learned.length > 0 ? (
          <div className="banner banner--ok">
            <strong>Already learned from your files.</strong> {learned.join(" · ")}. Questions those
            files answer have been removed from this interview.
          </div>
        ) : null}

        {pipeline ? (
          <div className="banner banner--ok">
            Interview submitted. {String(pipeline.extraction.facts_created ?? 0)} new facts proposed,{" "}
            {pipeline.contradictions_detected} contradictions detected, {pipeline.gaps_open} things
            still unknown.{" "}
            <Link to="/onboarding/review">See the summary →</Link>
          </div>
        ) : null}

        <div className="onboarding">
          {progress && progress.sections.length > 0 ? (
            <SectionNavigator
              sections={progress.sections}
              currentKey={progress.currentSectionKey}
              onSelect={(key) => control.current?.goToSection(key)}
            />
          ) : (
            <div />
          )}

          <div className="stack">
            {currentSection ? (
              <div className="section-intro">
                <h2>{currentSection.label}</h2>
                <p>{currentSection.why}</p>
                <div className="row small muted" style={{ marginTop: 8 }}>
                  <span>
                    {currentSection.answeredCount} of {currentSection.questionCount} answered here
                  </span>
                  {currentSection.requiredCount > 0 ? (
                    <span>
                      {currentSection.requiredCount - currentSection.requiredAnsweredCount} required
                      left
                    </span>
                  ) : (
                    <span>Nothing here is required</span>
                  )}
                  {nextSection ? (
                    <button
                      className="btn--small btn--ghost"
                      onClick={() => control.current?.goToSection(nextSection.key)}
                    >
                      Skip to {nextSection.label} →
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {coachOpen ? (
              <div style={{ height: 460 }}>
                <AssistantPanel
                  organizationId={organizationId}
                  mode="question_help"
                  operation="question_help"
                  interviewId={detail.id}
                  title="Assistant — about this interview"
                  onClose={() => setCoachOpen(false)}
                  onDraftSaved={() => {
                    session.reload();
                    responses.reload();
                  }}
                />
              </div>
            ) : null}

            {detail.survey ? (
              <SurveyRunner
                definition={detail.survey}
                initialData={detail.state?.data}
                initialPageNo={detail.state?.currentPageNo}
                readOnly={isComplete}
                organizationId={organizationId}
                sessionId={detail.id}
                projectId={detail.onboarding_project_id ?? undefined}
                deepDive={deepDive}
                uploads={uploadItems}
                onSave={handleSave}
                onComplete={handleComplete}
                onStatusChange={handleStatus}
                onProgress={setProgress}
                controlRef={control}
              />
            ) : (
              <Card>This interview has no compiled survey. Check the selected areas.</Card>
            )}

            <div className="row" style={{ justifyContent: "space-between" }}>
              <span className="small muted">
                Nothing here is permanent — every answer can be corrected later, and blank answers
                are simply recorded as things we still need to find out.
              </span>
              <button className="btn--small" onClick={() => setShowAnswers((value) => !value)}>
                {showAnswers ? "Hide saved answers" : "Show saved answers"}
              </button>
            </div>

            {showAnswers ? (
              <Card title={`Saved answers (${responses.data?.length ?? 0})`} flush>
                <div className="table-wrap" style={{ maxHeight: 420, overflowY: "auto" }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Question</th>
                        <th>Row</th>
                        <th>Answer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(responses.data ?? []).map((row) => (
                        <tr key={row.id}>
                          <td className="mono small">{row.semantic_id}</td>
                          <td className="small muted">{row.repeat_key || "—"}</td>
                          <td className="truncate">{displayValue(row.value?.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
